import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Check, EyeOff, Flag, Keyboard, ExternalLink, RefreshCw, ShieldX } from 'lucide-react';

import {
  QUEUE_LABELS,
  type ModerationAction,
  type QueueItem,
  type QueueReason,
  useModerateAssetsMutation,
  useReviewQueueQuery,
} from '@/client/api/assets/moderation';
import { signalName } from '@/client/api/assets/signals';
import AssetImage from '@/client/components/admin/Assets/AssetImage';
import ModerationDialog from '@/client/components/admin/Assets/ModerationDialog';
import Pagination from '@/client/components/common/Pagination';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const PAGE_SIZES = [12, 24, 48];

const Why = ({ item }: { item: QueueItem }) => (
  <div className="flex flex-wrap gap-1">
    {item.why.map((w) => (
      <span
        key={w.reason}
        title={w.note ?? (w.signal_key ? `${signalName(w.signal_key)}: ${w.label}${w.value != null ? ` (${w.value})` : ''}${w.reason_code ? ` · ${w.reason_code}` : ''}` : undefined)}
        className={cn(
          'rounded-full px-2 py-0.5 text-[11px] font-medium',
          ['nudity', 'violence', 'child_safety'].includes(w.reason) ? 'bg-red-50 text-red-700'
            : w.reason === 'flagged' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-800',
        )}
      >
        {QUEUE_LABELS[w.reason]}{w.label === 'unsafe' || w.label === 'review' ? ` · ${w.label}` : ''}
      </span>
    ))}
  </div>
);

/**
 * The Review queue: photos image analysis flagged, plus photos staff flagged
 * by hand, that nobody has approved since. Most severe first. Built for speed:
 * J/K to move, A approve, H hide, R reject, F flag, X select, O open.
 */
export const QualityControlPage = () => {
  useBreadcrumbs([{ label: 'Asset Management', href: '/admin/assets' }, { label: 'Review queue' }]);
  const [reason, setReason] = useState<QueueReason | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const { data, isLoading, isError, error, refetch, isFetching } = useReviewQueueQuery(reason, page, pageSize);
  const items = useMemo(() => data?.items ?? [], [data]);
  const moderate = useModerateAssetsMutation();
  const { hasPermission: canModerate, isSuperAdmin } = useHasPermission('asset.moderate');
  const allowed = canModerate || isSuperAdmin;

  const [focus, setFocus] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [dialog, setDialog] = useState<{ action: ModerationAction; asids: number[]; suggested?: string | null } | null>(null);

  useEffect(() => {
    setSelected(new Set());
    setFocus(0);
  }, [reason, page, pageSize]);
  useEffect(() => setFocus((f) => Math.min(f, Math.max(items.length - 1, 0))), [items.length]);

  const approve = useCallback(async (asids: number[]) => {
    try {
      const out = await moderate.mutateAsync({ asids, action: 'approve' });
      toast.success(`Approved ${out.ok} photo${out.ok === 1 ? '' : 's'}`);
      setSelected(new Set());
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not approve'));
    }
  }, [moderate]);

  const targets = (item?: QueueItem) => (selected.size ? Array.from(selected) : item?.asid != null ? [item.asid] : []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (dialog || !allowed || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const item = items[focus];
      const k = e.key.toLowerCase();
      if (k === 'j' || e.key === 'ArrowRight') setFocus((f) => Math.min(f + 1, items.length - 1));
      else if (k === 'k' || e.key === 'ArrowLeft') setFocus((f) => Math.max(f - 1, 0));
      else if (!item) return;
      else if (k === 'a') void approve(targets(item));
      else if (k === 'h') setDialog({ action: 'hide', asids: targets(item), suggested: item.suggested_reason });
      else if (k === 'r') setDialog({ action: 'reject', asids: targets(item), suggested: item.suggested_reason });
      else if (k === 'f') setDialog({ action: 'flag', asids: targets(item) });
      else if (k === 'x') setSelected((s) => { const n = new Set(s); n.has(item.asid!) ? n.delete(item.asid!) : n.add(item.asid!); return n; });
      else if (k === 'o' && item.display_url) window.open(item.display_url, '_blank', 'noopener');
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  useEffect(() => {
    document.getElementById(`rq-${items[focus]?.asid}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focus, items]);

  const tabs: (QueueReason | '')[] = ['', ...(data?.order ?? (Object.keys(QUEUE_LABELS) as QueueReason[]))];
  // A photo can be in several reasons; the API counts "all" distinctly.
  const total = data?.total_all ?? 0;

  return (
    <div className="min-h-screen space-y-5 bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Review queue</h1>
            <p className="mt-1 max-w-3xl text-sm text-gray-500">
              Photos image analysis flagged, and photos staff flagged by hand, that nobody has approved since. Most severe first.
              Hiding or rejecting takes the photo off its listing and tells the seller why.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')} /> Refresh
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {tabs.map((t) => {
            const n = t ? data?.counts[t] ?? 0 : total;
            return (
              <button
                key={t || 'all'}
                onClick={() => { setReason(t); setPage(1); }}
                className={cn(
                  'rounded-full border px-3 py-1 text-sm',
                  reason === t ? 'border-gray-900 bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-100',
                )}
              >
                {t ? QUEUE_LABELS[t] : 'All'} <span className="tabular-nums opacity-70">{n.toLocaleString()}</span>
              </button>
            );
          })}
        </div>

        {allowed && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-white px-4 py-2 text-sm">
            <label className="flex items-center gap-2 text-gray-600">
              <input
                type="checkbox"
                checked={items.length > 0 && items.every((i) => selected.has(i.asid!))}
                onChange={(e) => setSelected(e.target.checked ? new Set(items.map((i) => i.asid!)) : new Set())}
              />
              Select page
            </label>
            {selected.size > 0 && (
              <>
                <span className="font-medium">{selected.size} selected</span>
                <Button size="sm" variant="outline" disabled={moderate.isPending} onClick={() => void approve(Array.from(selected))}>
                  <Check className="mr-1.5 h-3.5 w-3.5" /> Approve
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDialog({ action: 'hide', asids: Array.from(selected), suggested: items.find((i) => selected.has(i.asid!))?.suggested_reason })}>
                  <EyeOff className="mr-1.5 h-3.5 w-3.5" /> Hide
                </Button>
                <Button size="sm" variant="destructive" onClick={() => setDialog({ action: 'reject', asids: Array.from(selected), suggested: items.find((i) => selected.has(i.asid!))?.suggested_reason })}>
                  <ShieldX className="mr-1.5 h-3.5 w-3.5" /> Reject
                </Button>
              </>
            )}
            <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-gray-500">
              <Keyboard className="h-3.5 w-3.5" /> J/K move · A approve · H hide · R reject · F flag · X select · O open
            </span>
          </div>
        )}

        {isLoading && !data ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-80" />)}
          </div>
        ) : isError ? (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-12 text-center text-sm text-red-600">
            {getApiErrorMessage(error, 'Could not load the review queue.')}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed bg-white px-4 py-16 text-center text-sm text-gray-500">
            Nothing waiting{reason ? ` for ${QUEUE_LABELS[reason].toLowerCase()}` : ''}.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {items.map((item, i) => (
              <div
                id={`rq-${item.asid}`}
                key={item.asid}
                onClick={() => setFocus(i)}
                className={cn(
                  'overflow-hidden rounded-lg border bg-white shadow-sm',
                  focus === i && 'ring-2 ring-gray-900',
                  selected.has(item.asid!) && 'ring-2 ring-blue-500',
                )}
              >
                <div className="relative aspect-square bg-muted">
                  <AssetImage asset={item} className="h-full w-full object-cover" />
                  {allowed && (
                    <input
                      type="checkbox"
                      aria-label={`Select photo ${item.asid}`}
                      className="absolute left-2 top-2 h-4 w-4"
                      checked={selected.has(item.asid!)}
                      onChange={() => setSelected((s) => { const n = new Set(s); n.has(item.asid!) ? n.delete(item.asid!) : n.add(item.asid!); return n; })}
                    />
                  )}
                  {item.tester === 1 && <span className="absolute right-2 top-2 rounded-full bg-gray-800/70 px-1.5 py-0.5 text-[10px] text-white">Test</span>}
                </div>
                <div className="space-y-2 p-3">
                  <Why item={item} />
                  <div className="truncate text-sm font-medium" title={item.listing_name ?? undefined}>
                    {item.listing_name ?? (item.eid ? `Listing #${item.eid}` : 'No listing')}
                  </div>
                  <div className="text-xs text-gray-500">#{item.asid}{item.eid ? ` · listing #${item.eid}` : ''}</div>
                  {item.description && <p className="line-clamp-2 text-xs text-gray-600" title={item.description}>{item.description}</p>}
                  {allowed && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      <Button size="sm" variant="outline" className="h-7 px-2" disabled={moderate.isPending} onClick={() => void approve([item.asid!])}>
                        <Check className="mr-1 h-3.5 w-3.5" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setDialog({ action: 'hide', asids: [item.asid!], suggested: item.suggested_reason })}>
                        <EyeOff className="mr-1 h-3.5 w-3.5" /> Hide
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-red-700" onClick={() => setDialog({ action: 'reject', asids: [item.asid!], suggested: item.suggested_reason })}>
                        <ShieldX className="mr-1 h-3.5 w-3.5" /> Reject
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2" title="Flag for a colleague" onClick={() => setDialog({ action: 'flag', asids: [item.asid!] })}>
                        <Flag className="h-3.5 w-3.5" />
                      </Button>
                      {item.display_url && (
                        <Button size="sm" variant="ghost" className="h-7 px-2" title="Open original" onClick={() => window.open(item.display_url!, '_blank', 'noopener')}>
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="rounded-lg border bg-white px-4 py-3">
          <Pagination
            total={data?.total ?? 0}
            page={page}
            itemsPerPage={pageSize}
            limit={pageSize}
            setPage={setPage}
            setItemsPerPage={(n: number) => { setPageSize(n); setPage(1); }}
            showButtons
            showSelect
            selectOptions={PAGE_SIZES}
          />
        </div>
      </div>

      <ModerationDialog
        action={dialog?.action ?? null}
        asids={dialog?.asids ?? []}
        suggestedReason={dialog?.suggested}
        reasons={data?.reasons}
        onClose={() => setDialog(null)}
        onDone={() => setSelected(new Set())}
      />
    </div>
  );
};
